/**
 * Frontend password validation utility
 * Mirrors backend validation logic for immediate UX feedback
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
  score: number;
}

export class PasswordValidator {
  private static readonly MIN_LENGTH = 8;
  private static readonly MAX_LENGTH = 128;
  private static readonly COMMON_WEAK_PATTERNS = [
    /^password/i,
    /^123456/,
    /^qwerty/i,
    /^abc123/i,
    /^admin/i,
    /^letmein/i,
    /^welcome/i,
    /^monkey/i,
    /^dragon/i,
    /^master/i,
    /^shadow/i,
    /^superman/i,
    /^michael/i,
    /^football/i,
    /^baseball/i,
    /^liverpool/i,
    /^jordan/i,
    /^harley/i,
    /^ranger/i,
    /^buster/i,
    /^johnny/i,
    /^badboy/i,
    /^iwantu/i,
    /^slayer/i,
    /^chris/i,
    /^fucking/i,
    /^panties/i,
    /^summer/i,
    /^nikita/i,
    /^maggie/i,
    /^biteme/i
  ];

  /**
   * Validate password and return comprehensive results
   */
  static validatePassword(password: string): PasswordValidationResult {
    const errors: string[] = [];
    let score = 0;

    // Length validation
    if (password.length < this.MIN_LENGTH) {
      errors.push(`Password must be at least ${this.MIN_LENGTH} characters long`);
    } else if (password.length > this.MAX_LENGTH) {
      errors.push(`Password must not exceed ${this.MAX_LENGTH} characters`);
    } else {
      // Length scoring
      if (password.length >= 8) score += 1;
      if (password.length >= 12) score += 1;
    }

    // Character type validation
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    } else {
      score += 1;
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    } else {
      score += 1;
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one digit');
    } else {
      score += 1;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)');
    } else {
      score += 1;
    }

    // Common weak patterns validation
    for (const pattern of this.COMMON_WEAK_PATTERNS) {
      if (pattern.test(password)) {
        errors.push('Password contains common weak patterns. Please choose a more secure password');
        break;
      }
    }

    // Determine strength based on score
    let strength: 'weak' | 'medium' | 'strong';
    if (score <= 2) {
      strength = 'weak';
    } else if (score <= 4) {
      strength = 'medium';
    } else {
      strength = 'strong';
    }

    return {
      isValid: errors.length === 0,
      errors,
      strength,
      score
    };
  }

  /**
   * Get password strength color for UI display
   */
  static getStrengthColor(strength: 'weak' | 'medium' | 'strong'): string {
    switch (strength) {
      case 'weak':
        return '#f44336'; // Red
      case 'medium':
        return '#ff9800'; // Orange
      case 'strong':
        return '#4caf50'; // Green
      default:
        return '#9e9e9e'; // Gray
    }
  }

  /**
   * Get password strength text for UI display
   */
  static getStrengthText(strength: 'weak' | 'medium' | 'strong'): string {
    switch (strength) {
      case 'weak':
        return 'Weak';
      case 'medium':
        return 'Medium';
      case 'strong':
        return 'Strong';
      default:
        return '';
    }
  }
}